// Cart Attribute Manager - Nio
// This script blocks checkout until cart attributes are set correctly.
// It ensures that all necessary attributes are set based on the cart contents.
// It also verifies that the attributes are set correctly before allowing checkout.

document.addEventListener('DOMContentLoaded', function() {
    // Initialize cart attribute manager
    initCartAttributeManager();

    // Intercept all checkout clicks
    interceptCheckoutButtons();
});

// Store the cocktail data in localStorage to avoid repeated fetching
async function fetchAndStoreCocktailData() {
    // Only fetch if we don't have cached data or it's older than 24 hours
    const cachedData = localStorage.getItem('cocktailData');
    const cachedTimestamp = localStorage.getItem('cocktailDataTimestamp');
    const now = Date.now();

    if (cachedData && cachedTimestamp && (now - parseInt(cachedTimestamp) < 24 * 60 * 60 * 1000)) {
        return JSON.parse(cachedData);
    }

    try {
        // Fetch product data - this depends on having a product JSON endpoint
        // You'll need to create a products.json template that outputs your cocktail products
        const response = await fetch('/collections/all/products.json?limit=250');
        const data = await response.json();

        // Filter for cocktail products and create mapping
        const cocktailProducts = data.products.filter(p =>
            p.product_type === 'Cocktail' || p.tags.includes('Cocktail')
        );

        const cocktailMapping = {};
        cocktailProducts.forEach(product => {
            if (product.variants && product.variants.length > 0) {
                product.variants.forEach(variant => {
                    if (variant.sku) {
                        cocktailMapping[variant.title] = variant.sku;
                    }
                });
            }
        });

        // Store in localStorage with timestamp
        localStorage.setItem('cocktailData', JSON.stringify(cocktailMapping));
        localStorage.setItem('cocktailDataTimestamp', now.toString());

        return cocktailMapping;
    } catch (error) {
        console.error('Error fetching cocktail data:', error);

        // Fallback to basic mapping if fetch fails
        return {
            'Clementea': 'CIT0010220',
            'COCKTAIL COSMOPOLITAN': 'CIN0044070',
            'COCKTAIL MAI TAI': 'CIT0041727',
            'COCKTAIL MANHATTAN': 'CIN0044094',
            'COCKTAIL NEGRONI': 'CIT0011601',
            'COCKTAIL REVOLVER': 'CIT0078203',
            'COCKTAIL SIDECAR': 'CIN0040423',
            'COCKTAIL VODKA SOUR': 'CIT2044063',
            'Cuban Negroni': 'CIT0051998'
        };
    }
}

// Main initialization function
async function initCartAttributeManager() {
    try {
        // Get cocktail mapping
        const cocktailMapping = await fetchAndStoreCocktailData();

        // Ensure cart attributes are set
        await ensureCartAttributes(cocktailMapping);
    } catch (error) {
        console.error('Error initializing cart attribute manager:', error);
    }
}

// Intercept all checkout buttons
function interceptCheckoutButtons() {
    // Listen for clicks on checkout buttons
    document.addEventListener('click', async function(event) {
        // Check if click target is a checkout button or link
        const checkoutButton = event.target.closest('[href="/checkout"], [action="/cart/checkout"], [name="checkout"], .checkout-button');

        if (checkoutButton) {
            event.preventDefault();
            event.stopPropagation();

            try {
                // Show loading overlay
                showLoadingOverlay('Processing your order...');

                // Ensure attributes are set
                const cocktailMapping = await fetchAndStoreCocktailData();
                await ensureCartAttributes(cocktailMapping);

                // Continue to checkout
                hideLoadingOverlay();

                window.location.href = '/checkout';
            } catch (error) {
                console.error('Error during checkout:', error);
                hideLoadingOverlay();
                alert('There was an issue processing your cart. Please try again.');
            }
        }
    }, true);
}

// Ensure cart attributes are set correctly
async function ensureCartAttributes(cocktailMapping) {
    // Get current cart
    const cart = await fetchCart();

    // Find box items
    const boxItems = cart.items.filter(item =>
        item.product_type === 'Build a Box' ||
        item.product_title.includes('Box')
    );

    if (boxItems.length === 0) {
        return; // No box items, nothing to do
    }

    // Generate attributes
    const attributes = {};
    let boxCounter = 1;

    boxItems.forEach(item => {
        const boxKey = `box${boxCounter}-${item.sku}`;
        boxCounter++;

        if (item.properties) {
            const cocktailSkus = [];

            // Process properties
            Object.entries(item.properties).forEach(([key, value]) => {
                if (key.includes('Cocktail') && !key.startsWith('_')) {
                    // Get SKU for this cocktail
                    const sku = getCocktailSku(value, cocktailMapping);
                    if (sku) {
                        cocktailSkus.push(sku);
                    }
                }
            });

            if (cocktailSkus.length > 0) {
                attributes[boxKey] = cocktailSkus.join(',');
            }
        }
    });

    // Check if attributes need updating
    let needsUpdate = false;
    for (const [key, value] of Object.entries(attributes)) {
        if (!cart.attributes[key] || cart.attributes[key] !== value) {
            needsUpdate = true;
            break;
        }
    }

    // Update cart attributes if needed
    if (needsUpdate) {
        await updateCartAttributes(attributes);

        // Verify update
        const updatedCart = await fetchCart();
        for (const [key, value] of Object.entries(attributes)) {
            if (!updatedCart.attributes[key] || updatedCart.attributes[key] !== value) {
                throw new Error(`Failed to set attribute: ${key}`);
            }
        }
    }
}

// Helper function to get SKU for cocktail
function getCocktailSku(cocktailName, mapping) {
    return mapping[cocktailName] || '';
}

// Fetch cart data
async function fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
}

// Update cart attributes
async function updateCartAttributes(attributes) {
    const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes }),
    });
    return response.json();
}

// UI Helper functions
function showLoadingOverlay(message) {
    // Create overlay if it doesn't exist
    let overlay = document.getElementById('cart-loading-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'cart-loading-overlay';
        overlay.innerHTML = `
            <div class="loading-content">
            <div class="loading-spinner"></div>
            <p id="loading-message">${message}</p>
            </div>
        `;
        document.body.appendChild(overlay);

        // Add styles
        const style = document.createElement('style');
        style.textContent = `
            #cart-loading-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            }
            .loading-content {
            background: white;
            padding: 20px;
            border-radius: 5px;
            text-align: center;
            }
            .loading-spinner {
            display: inline-block;
            width: 30px;
            height: 30px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #141414;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 10px;
            }
            @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
    } else {
        document.getElementById('loading-message').textContent = message;
        overlay.style.display = 'flex';
    }
}

function hideLoadingOverlay() {
    const overlay = document.getElementById('cart-loading-overlay');
    if (overlay) {
        overlay.style.display = 'none';
    }
}